import {useCallback, useEffect, useMemo, useState} from 'react'
import type {AxiosResponse} from 'axios'
import {getAuth} from '../modules/auth/core/AuthHelpers'
import {axiosClientAuth} from '../utils'

export const ENABLE_NOTIFICATIONS = false

export interface Notification {
  id: string
  title: string
  message: string
  userEmail?: string
  status?: string
  priority?: string
  createdAt: string
  isRead: boolean
}

/** Relative to REACT_APP_API_URL (same pattern as rest of app) */
const NOTIFICATIONS_LIST = 'auth/r1/notifications/'
const NOTIFICATIONS_UNREAD = 'auth/r1/notifications/unread-count/'
const NOTIFICATIONS_READ_ONE = 'auth/r1/notifications/read/'
const NOTIFICATIONS_READ_ALL = 'auth/r1/notifications/read-all/'
const NOTIFICATIONS_SYNC_EVENT = 'payairo:notifications:sync'

/** Backend may return status, success, or plain 200/204 */
const isTruthyApiResponse = (res: AxiosResponse): boolean => {
  const st = res?.status
  if (st === 204) return true
  if (st !== 200 && st !== 201) return false
  const d = res?.data as Record<string, unknown> | undefined | null
  if (d == null || typeof d !== 'object') return true
  if ('status' in d && d.status === false) return false
  if ('success' in d && d.success === false) return false
  if (d.status === true || d.success === true) return true
  if (d.message && typeof d.message === 'string' && /read|success|ok/i.test(d.message)) return true
  return true
}

// Shared unread-count store across multiple hook instances (navbar + page + bell).
let globalUnreadCount = 0
const unreadCountListeners = new Set<(count: number) => void>()
let globalUnreadCountIntervalStarted = false

const setGlobalUnreadCount = (count: number) => {
  globalUnreadCount = count
  unreadCountListeners.forEach((fn) => fn(count))
}

const parseBooleanMaybe = (value: unknown) => {
  if (typeof value === 'boolean') return value
  if (value === 0 || value === '0') return false
  if (value === 1 || value === '1') return true
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase()
    if (lower === 'true' || lower === 'yes') return true
    if (lower === 'false' || lower === 'no') return false
  }
  if (value === null || value === undefined) return false
  return Boolean(value)
}

const mapNotificationFromApi = (raw: any): Notification => {
  const createdAt = raw?.created_at ?? raw?.createdAt ?? raw?.createdAt
  let isReadRaw: unknown = raw?.is_read ?? raw?.isRead ?? raw?.read
  if (isReadRaw === undefined || isReadRaw === null) {
    isReadRaw = raw?.notification?.is_read ?? raw?.notification?.isRead
  }

  return {
    id: String(raw?.id ?? raw?.notification_id ?? ''),
    title: raw?.title ?? '',
    message: raw?.message ?? '',
    userEmail: raw?.user_email ?? raw?.userEmail,
    status: raw?.status,
    priority: raw?.priority,
    createdAt: String(createdAt ?? ''),
    isRead: parseBooleanMaybe(isReadRaw),
  }
}

export const formatTimeAgo = (isoDate?: string) => {
  if (!isoDate) return 'Just now'
  const parsed = new Date(isoDate)
  if (Number.isNaN(parsed.getTime())) return 'Just now'

  const diffMs = Date.now() - parsed.getTime()
  const diffSec = Math.floor(diffMs / 1000)

  if (diffSec < 0) return 'Just now'
  if (diffSec < 60) return 'Just now'

  if (diffSec < 3600) {
    const minutes = Math.floor(diffSec / 60)
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  }

  if (diffSec < 86400) {
    const hours = Math.floor(diffSec / 3600)
    return `${hours} hour${hours === 1 ? '' : 's'} ago`
  }

  const days = Math.floor(diffSec / 86400)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

export const useNotifications = (autoRefresh = true) => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingReadAll, setLoadingReadAll] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unreadCount, setUnreadCount] = useState<number>(globalUnreadCount)

  const fetchUnreadCount = useCallback(async () => {
    if (!ENABLE_NOTIFICATIONS) return 0
    try {
      const auth = getAuth()
      const token = auth?.access_token || localStorage.getItem('access_token')
      if (!token) {
        setGlobalUnreadCount(0)
        return 0
      }

      const response = await axiosClientAuth.get(NOTIFICATIONS_UNREAD)

      const rawCount =
        response?.data?.data?.unread_count ??
        response?.data?.unread_count ??
        response?.data?.data?.count
      const parsed =
        typeof rawCount === 'number'
          ? rawCount
          : rawCount != null
          ? parseInt(String(rawCount), 10)
          : 0
      const safeCount = Number.isFinite(parsed) && parsed > 0 ? parsed : 0

      setGlobalUnreadCount(safeCount)
      return safeCount
    } catch (e) {
      console.error('Error fetching unread count:', e)
      return globalUnreadCount
    }
  }, [])

  useEffect(() => {
    const listener = (count: number) => setUnreadCount(count)
    unreadCountListeners.add(listener)
    setUnreadCount(globalUnreadCount)
    return () => {
      unreadCountListeners.delete(listener)
    }
  }, [])

  useEffect(() => {
    if (!ENABLE_NOTIFICATIONS || globalUnreadCountIntervalStarted) return
    globalUnreadCountIntervalStarted = true

    // Immediately on first mount
    void fetchUnreadCount()

    // Then every 30 seconds (silent)
    const intervalId = window.setInterval(() => {
      void fetchUnreadCount()
    }, 30000)

    // Note: This is a shared singleton; do not clear interval on unmount.
    return () => {}
  }, [fetchUnreadCount])

  const fetchNotifications = useCallback(async () => {
    if (!ENABLE_NOTIFICATIONS) {
      setNotifications([])
      setLoading(false)
      return
    }
    try {
      setError(null)
      setLoading(true)

      const auth = getAuth()
      const token = auth?.access_token || localStorage.getItem('access_token')
      if (!token) {
        setNotifications([])
        setError('No access token found for notifications')
        return
      }

      const response = await axiosClientAuth.get(NOTIFICATIONS_LIST)

      const data = response?.data
      const rawNotifications =
        data?.notifications ?? data?.data ?? data?.items ?? (Array.isArray(data) ? data : [])

      const mapped: Notification[] = Array.isArray(rawNotifications)
        ? rawNotifications.map(mapNotificationFromApi)
        : []

      setNotifications(mapped)
    } catch (e) {
      console.error('Error fetching notifications:', e)
      setError('Failed to load notifications')
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchNotifications()
    if (!autoRefresh) return

    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [fetchNotifications, autoRefresh])

  // Keep multiple hook instances (Navbar bell + Notifications page) in sync.
  useEffect(() => {
    if (!ENABLE_NOTIFICATIONS) return
    const onSync = (event: any) => {
      const id = event?.detail?.id as string | undefined
      if (!id) return

      if (id === '__ALL__') {
        setNotifications((prev) => prev.map((n) => ({...n, isRead: true})))
        return
      }

      setNotifications((prev) => prev.map((n) => (n.id === id ? {...n, isRead: true} : n)))
    }

    window.addEventListener(NOTIFICATIONS_SYNC_EVENT, onSync)
    return () => window.removeEventListener(NOTIFICATIONS_SYNC_EVENT, onSync)
  }, [])

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const at = a?.createdAt ? new Date(a.createdAt).getTime() : 0
      const bt = b?.createdAt ? new Date(b.createdAt).getTime() : 0
      return bt - at
    })
  }, [notifications])

  const markAsRead = useCallback(
    (id?: string) => {
      if (!id) return
      setNotifications((prev) => prev.map((n) => (n.id === id ? {...n, isRead: true} : n)))
      window.dispatchEvent(new CustomEvent(NOTIFICATIONS_SYNC_EVENT, {detail: {id}}))
      void fetchUnreadCount()
    },
    [fetchUnreadCount]
  )

  const markAsReadApi = useCallback(
    async (id?: string) => {
      if (!id) return false
      try {
        const auth = getAuth()
        const token = auth?.access_token || localStorage.getItem('access_token')
        if (!token) return false

        const response = await axiosClientAuth.post(NOTIFICATIONS_READ_ONE, {
          notification_id: id,
        })

        if (isTruthyApiResponse(response)) {
          setNotifications((prev) => prev.map((n) => (n.id === id ? {...n, isRead: true} : n)))
          window.dispatchEvent(new CustomEvent(NOTIFICATIONS_SYNC_EVENT, {detail: {id}}))
          await fetchUnreadCount()
          return true
        }

        return false
      } catch (e) {
        // Silent for user, but log for debugging.
        console.error('Error marking notification as read:', e)
        return false
      }
    },
    [fetchUnreadCount]
  )

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({...n, isRead: true})))
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_SYNC_EVENT, {detail: {id: '__ALL__'}}))
  }, [])

  const markAllAsReadApi = useCallback(async () => {
    try {
      setLoadingReadAll(true)
      const auth = getAuth()
      const token = auth?.access_token || localStorage.getItem('access_token')

      if (!token) throw new Error('No access token')

      const response = await axiosClientAuth.post(NOTIFICATIONS_READ_ALL, {})

      if (isTruthyApiResponse(response)) {
        setNotifications((prev) => prev.map((n) => ({...n, isRead: true})))
        window.dispatchEvent(new CustomEvent(NOTIFICATIONS_SYNC_EVENT, {detail: {id: '__ALL__'}}))
        await fetchUnreadCount()
        return true
      }
      return false
    } catch (e) {
      console.error('Error marking all as read:', e)
      return false
    } finally {
      setLoadingReadAll(false)
    }
  }, [fetchUnreadCount])

  return {
    notifications: sortedNotifications,
    loading,
    loadingReadAll,
    error,
    unreadCount,
    refresh: fetchNotifications,
    markAsRead,
    markAsReadApi,
    markAllAsRead,
    markAllAsReadApi,
    fetchUnreadCount,
  }
}
